/**
 * TrailerModal Component
 * Displays movie trailers in a modal with YouTube embed
 */

import { useEffect, useState } from 'react';
import { getMovieVideos, type MovieVideo } from '../api';
import ModalPortal from './ModalPortal';

interface TrailerModalProps {
  tmdbId: number;
  movieTitle: string;
  onClose: () => void;
}

// Validate YouTube video ID format (alphanumeric, hyphens, underscores, 11 chars)
function isValidYouTubeKey(key: string): boolean {
  return /^[A-Za-z0-9_-]{11}$/.test(key);
}

export default function TrailerModal({ tmdbId, movieTitle, onClose }: TrailerModalProps) {
  const [videos, setVideos] = useState<MovieVideo[]>([]);
  const [selectedVideo, setSelectedVideo] = useState<MovieVideo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadVideos() {
      try {
        setLoading(true);
        const videoData = await getMovieVideos(tmdbId);
        // Filter for valid YouTube keys
        const validVideos = videoData.filter((v) => isValidYouTubeKey(v.key));
        setVideos(validVideos);
        // Auto-select the first video (should be the most relevant trailer)
        if (validVideos.length > 0) {
          setSelectedVideo(validVideos[0]);
        }
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load trailers');
      } finally {
        setLoading(false);
      }
    }

    loadVideos();
  }, [tmdbId]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <ModalPortal>
      <div className="modal-overlay" onClick={handleOverlayClick}>
        <div className="trailer-modal">
          <div className="trailer-modal-header">
            <h2>{movieTitle} - Trailers</h2>
            <button onClick={onClose} className="modal-close" aria-label="Close">
              ×
            </button>
          </div>

          <div className="trailer-modal-content">
            {loading && <div className="empty">Loading trailers...</div>}

            {error && (
              <div className="empty">
                <p>{error}</p>
              </div>
            )}

            {!loading && !error && videos.length === 0 && (
              <div className="empty">
                <p>No trailers available for this movie.</p>
              </div>
            )}

            {!loading && !error && videos.length > 0 && selectedVideo && (
              <>
                <div className="trailer-player">
                  <iframe
                    src={`https://www.youtube.com/embed/${selectedVideo.key}?autoplay=1&rel=0`}
                    title={selectedVideo.name}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>

                {videos.length > 1 && (
                  <div className="trailer-selector">
                    <h3>Available Trailers</h3>
                    <div className="trailer-list">
                      {videos.map((video) => (
                        <button
                          key={video.id}
                          onClick={() => setSelectedVideo(video)}
                          className={`trailer-item ${selectedVideo.id === video.id ? 'active' : ''}`}
                        >
                          <div className="trailer-item-info">
                            <span className="trailer-item-name">{video.name}</span>
                            <div className="trailer-item-meta">
                              <span className="trailer-item-type">{video.type}</span>
                              {video.official && (
                                <span className="trailer-item-badge">Official</span>
                              )}
                            </div>
                          </div>
                          <svg
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            className="trailer-item-icon"
                          >
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}
